import type { IncidentListResponse, IncidentListSortKey } from '@prerender/diagnostics-api';
import type { ReactNode } from 'react';
import { Alert } from 'react-bootstrap';

import Loading from '../../app/components/Loading';
import DiagnosticsPage from './DiagnosticsPage';
import IncidentFilters from './IncidentFilters';
import type { IncidentListParams } from './incidentListParams';
import { incidentsErrorMessage } from './incidentsErrorMessage';
import IncidentsTable from './IncidentsTable';
import type { Loadable } from './loadable';
import type { IncidentListFilterPatch } from './useIncidentListParams';

import './diagnostics.css';

type IncidentsListViewProps = {
  incidents: Loadable<IncidentListResponse>;
  refreshing: boolean;
  params: IncidentListParams;
  now: Date;
  onFiltersChange: (patch: IncidentListFilterPatch) => void;
  onSort: (key: IncidentListSortKey) => void;
  onPageChange: (page: number) => void;
  actions: ReactNode;
  runNotice: ReactNode;
  counters: ReactNode;
};

function IncidentsListView({
  incidents,
  refreshing,
  params,
  now,
  onFiltersChange,
  onSort,
  onPageChange,
  actions,
  runNotice,
  counters,
}: IncidentsListViewProps): JSX.Element {
  const results = (): ReactNode => {
    if (incidents.status === 'failed') {
      return <Alert variant="danger">{incidentsErrorMessage(incidents.error)}</Alert>;
    }

    if (incidents.status !== 'loaded') {
      return <Loading text="Loading incidents" />;
    }

    return (
      <IncidentsTable
        incidents={incidents.data}
        refreshing={refreshing}
        params={params}
        now={now}
        onFiltersChange={onFiltersChange}
        onSort={onSort}
        onPageChange={onPageChange}
      />
    );
  };

  return (
    <DiagnosticsPage
      action={
        <div className="diagnostics-header-actions" role="group" aria-label="Page actions">
          {actions}
        </div>
      }
    >
      {runNotice}
      {counters}
      <IncidentFilters params={params} onChange={onFiltersChange} />
      {results()}
    </DiagnosticsPage>
  );
}

export default IncidentsListView;
